import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Statistics = () => {
  const [loginHistory, setLoginHistory] = useState([]);
  const [statistics, setStatistics] = useState({ byUser: [], byDate: [] });
  // Card usage statistics disabled - no longer displaying fuel consumption data
  // const [cardUsageStats, setCardUsageStats] = useState({ 
  //   summary: {}, 
  //   byCard: [], 
  //   byUser: [], 
  //   byDate: [] 
  // });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [filters, setFilters] = useState({
    selectedUsers: null,
    startDate: null,
    endDate: null
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchUsers = useCallback(async () => {
    try {
      // Fetch end users from app_logs (unique userIds)
      const response = await axios.get(`${API_BASE_URL}/logs/filters`, getAuthHeaders());
      const userIds = response.data.userIds || [];
      // Convert to user objects format
      const endUsers = userIds.map(userId => ({
        _id: userId,
        email: userId,
        name: userId,
        role: 'USER'
      }));
      setUsers(endUsers);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchLoginHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        userType: 'enduser',
        limit: 1000,
        skip: 0
      };

      if (filters.selectedUsers && filters.selectedUsers.length > 0) {
        params.userIds = filters.selectedUsers.map(u => u._id || u.email || u).join(',');
      }

      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString();
      }

      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString();
      }

      const response = await axios.get(`${API_BASE_URL}/statistics/login-history`, {
        ...getAuthHeaders(),
        params
      });
      
      setLoginHistory(response.data.loginHistory || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch login history');
      console.error('Failed to fetch login history:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        userType: 'enduser'
      };

      if (filters.selectedUsers && filters.selectedUsers.length > 0) {
        params.userIds = filters.selectedUsers.map(u => u._id || u.email || u).join(',');
      }

      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString();
      }

      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString();
      }

      const response = await axios.get(`${API_BASE_URL}/statistics/login-stats`, {
        ...getAuthHeaders(),
        params
      });
      
      setStatistics(response.data || { byUser: [], byDate: [] });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch statistics');
      console.error('Failed to fetch statistics:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Card usage statistics disabled - no longer fetching fuel consumption data
  // const fetchCardUsage = useCallback(async () => {
  //   setLoading(true);
  //   setError('');
  //   try {
  //     const params = {};

  //     if (filters.selectedUsers && filters.selectedUsers.length > 0) {
  //       params.userIds = filters.selectedUsers.map(u => u._id || u.email || u).join(',');
  //     }

  //     if (filters.startDate) {
  //       params.startDate = filters.startDate.toISOString();
  //     }

  //     if (filters.endDate) {
  //       params.endDate = filters.endDate.toISOString();
  //     }

  //     const response = await axios.get(`${API_BASE_URL}/statistics/card-usage`, {
  //       ...getAuthHeaders(),
  //       params
  //     });
      
  //     setCardUsageStats(response.data || { 
  //       summary: {}, 
  //       byCard: [], 
  //       byUser: [], 
  //       byDate: [] 
  //     });
  //   } catch (err) {
  //     setError(err.response?.data?.message || 'Failed to fetch card usage statistics');
  //     console.error('Failed to fetch card usage statistics:', err);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [filters]);

  useEffect(() => {
    // Fetch all data in parallel
    fetchLoginHistory();
    fetchStatistics();
    // Card usage statistics disabled - no longer fetching fuel consumption data
    // fetchCardUsage();
  }, [filters, fetchLoginHistory, fetchStatistics]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      selectedUsers: null,
      startDate: null,
      endDate: null
    });
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {};

      if (filters.selectedUsers && filters.selectedUsers.length > 0) {
        params.userIds = filters.selectedUsers.map(u => u._id || u.email || u).join(',');
      }

      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString();
      }

      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString();
      }

      // Fetch PDF with axios
      const response = await axios.get(`${API_BASE_URL}/statistics/export-pdf`, {
        ...getAuthHeaders(),
        params,
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `statistics-report-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export PDF');
      console.error('Failed to export PDF:', err);
    } finally {
      setLoading(false);
    }
  };

  const userBodyTemplate = (rowData) => {
    const user = rowData.user || {};
    return (
      <div>
        <div style={{ fontWeight: '600', color: '#2c3e50' }}>
          {user.name || 'Unknown User'}
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          {user.email || 'Unknown'}
        </div>
        {user.role && (
          <span style={{
            display: 'inline-block',
            marginTop: '0.25rem',
            padding: '0.25rem 0.5rem',
            backgroundColor: user.role === 'masteradmin' ? '#fff3cd' : '#e3f2fd',
            color: user.role === 'masteradmin' ? '#856404' : '#1565c0',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {user.role === 'masteradmin' ? 'Master Admin' : 'Admin'}
          </span>
        )}
      </div>
    );
  };

  const dateBodyTemplate = (rowData) => {
    if (!rowData.loginTime) return '-';
    const date = new Date(rowData.loginTime);
    return (
      <div>
        <div style={{ fontWeight: '500' }}>
          {date.toLocaleDateString()}
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          {date.toLocaleTimeString()}
        </div>
      </div>
    );
  };

  const statsUserBodyTemplate = (rowData) => {
    const user = rowData.user || {};
    return (
      <div>
        <div style={{ fontWeight: '600', color: '#2c3e50' }}>
          {user.name || 'Unknown User'}
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          {user.email || 'Unknown'}
        </div>
      </div>
    );
  };

  const statsDateBodyTemplate = (rowData) => {
    if (!rowData.date) return '-';
    return new Date(rowData.date).toLocaleDateString();
  };

  const statsLoginCountBodyTemplate = (rowData) => {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.375rem 0.875rem',
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        borderRadius: '16px',
        fontSize: '0.875rem',
        fontWeight: '600'
      }}>
        {rowData.loginCount || 0}
      </span>
    );
  };

  const statsDateCountBodyTemplate = (rowData) => {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.375rem 0.875rem',
        backgroundColor: '#d4edda',
        color: '#155724',
        borderRadius: '16px',
        fontSize: '0.875rem',
        fontWeight: '600'
      }}>
        {rowData.loginCount || 0}
      </span>
    );
  };

  const userOptions = users.map(user => {
    // For end users, userId is the identifier
    const userId = user._id || user.email || user;
    return {
      label: userId,
      value: { _id: userId, email: userId, name: userId, role: 'USER' }
    };
  });

  return (
    <div style={{ 
      padding: '2rem 2.5rem', 
      maxWidth: '1400px', 
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      <div style={{ 
        marginBottom: '2.5rem', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div>
          <h1 style={{ 
            marginBottom: '0.5rem', 
            fontSize: '2rem', 
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            Statistics
          </h1>
          <p style={{ 
            marginBottom: 0, 
            color: '#6c757d',
            fontSize: '0.95rem'
          }}>
            View login history and statistics for end users
          </p>
        </div>
        <Button
          label="Export to PDF"
          icon="pi pi-file-pdf"
          iconPos="left"
          onClick={handleExportPDF}
          className="p-button-lg"
          disabled={loading}
          style={{ 
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        />
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setError('')} 
        />
      )}

      {/* Filters */}
      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        padding: '1.5rem'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem',
          alignItems: 'flex-start'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.75rem', 
              fontWeight: '600',
              color: '#495057',
              fontSize: '0.95rem'
            }}>
              User IDs
            </label>
            <MultiSelect
              value={filters.selectedUsers}
              options={userOptions}
              onChange={(e) => handleFilterChange('selectedUsers', e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Select user IDs (leave empty for all)"
              display="chip"
              style={{ width: '100%' }}
              inputStyle={{ 
                height: '2.5rem', 
                minHeight: '2.5rem',
                padding: '0.5rem 0.75rem',
                fontSize: '0.95rem',
                lineHeight: '1.5'
              }}
              panelStyle={{ minWidth: '300px' }}
              filter
              disabled={loading}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.75rem', 
              fontWeight: '600',
              color: '#495057',
              fontSize: '0.95rem'
            }}>
              Start Date
            </label>
            <Calendar
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.value)}
              showIcon
              dateFormat="yy-mm-dd"
              style={{ width: '100%' }}
              inputStyle={{ 
                width: '100%', 
                height: '2.5rem', 
                padding: '0.5rem 0.75rem',
                fontSize: '0.95rem'
              }}
              iconStyle={{ height: '2.5rem', width: '2.5rem' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.75rem', 
              fontWeight: '600',
              color: '#495057',
              fontSize: '0.95rem'
            }}>
              End Date
            </label>
            <Calendar
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.value)}
              showIcon
              dateFormat="yy-mm-dd"
              style={{ width: '100%' }}
              inputStyle={{ 
                width: '100%', 
                height: '2.5rem', 
                padding: '0.5rem 0.75rem',
                fontSize: '0.95rem'
              }}
              iconStyle={{ height: '2.5rem', width: '2.5rem' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <Button
              label="Clear Filters"
              icon="pi pi-filter-slash"
              iconPos="left"
              onClick={clearFilters}
              className="p-button-outlined"
              style={{ 
                width: '100%', 
                height: '2.5rem',
                padding: '0.5rem 1rem',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            />
          </div>
        </div>
      </Card>

      {/* Login History */}
      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        padding: '1.5rem'
      }}>
        <h2 style={{ 
          marginBottom: '1.5rem', 
          fontSize: '1.5rem', 
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Login History
        </h2>
        <DataTable
          value={loginHistory}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage="No login history found"
          style={{ width: '100%' }}
        >
          <Column 
            field="user" 
            header="User" 
            body={userBodyTemplate}
            style={{ minWidth: '250px' }} 
          />
          <Column 
            field="loginTime" 
            header="Login Time" 
            body={dateBodyTemplate}
            sortable 
            style={{ minWidth: '200px' }} 
          />
        </DataTable>
      </Card>

      {/* Statistics by User */}
      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        padding: '1.5rem'
      }}>
        <h2 style={{ 
          marginBottom: '1.5rem', 
          fontSize: '1.5rem', 
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Statistics by User
        </h2>
        <DataTable
          value={statistics.byUser || []}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No statistics found"
          style={{ width: '100%' }}
        >
          <Column 
            field="user" 
            header="User" 
            body={statsUserBodyTemplate}
            style={{ minWidth: '250px' }} 
          />
          <Column 
            field="loginCount" 
            header="Total Logins" 
            body={statsLoginCountBodyTemplate}
            sortable 
            style={{ minWidth: '150px' }} 
          />
          <Column 
            field="firstLogin" 
            header="First Login" 
            sortable 
            style={{ minWidth: '180px' }}
            body={(rowData) => rowData.firstLogin ? new Date(rowData.firstLogin).toLocaleString() : 'Never'}
          />
          <Column 
            field="lastLogin" 
            header="Last Login" 
            sortable 
            style={{ minWidth: '180px' }}
            body={(rowData) => rowData.lastLogin ? new Date(rowData.lastLogin).toLocaleString() : 'Never'}
          />
        </DataTable>
      </Card>

      {/* Statistics by Date */}
      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        padding: '1.5rem'
      }}>
        <h2 style={{ 
          marginBottom: '1.5rem', 
          fontSize: '1.5rem', 
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Statistics by Date (Last 30 Days)
        </h2>
        <DataTable
          value={statistics.byDate || []}
          loading={loading}
          paginator
          rows={30}
          rowsPerPageOptions={[10, 30, 50]}
          emptyMessage="No statistics found"
          style={{ width: '100%' }}
        >
          <Column 
            field="date" 
            header="Date" 
            body={statsDateBodyTemplate}
            sortable 
            style={{ minWidth: '150px' }} 
          />
          <Column 
            field="loginCount" 
            header="Logins" 
            body={statsDateCountBodyTemplate}
            sortable 
            style={{ minWidth: '120px' }} 
          />
        </DataTable>
      </Card>

      {/* Card Usage section disabled - no longer displaying fuel consumption data */}
    </div>
  );
};

export default Statistics;
